// Real-time cryptocurrency price service using CryptoCompare API
// Professional-grade API with real-time multi-exchange data
// Free, no API key required, no geo-restrictions

interface PriceData {
  usd: number;
  last_updated_at: number;
}

interface PriceCache {
  prices: Record<string, PriceData>;
  lastUpdate: number;
}

// Cache to avoid excessive API calls
const priceCache: PriceCache = {
  prices: {},
  lastUpdate: 0,
};

// Cache duration: 10 seconds (real-time updates)
const CACHE_DURATION = 10 * 1000;

/**
 * Fetch live prices from CryptoCompare API (batch mode)
 * CryptoCompare aggregates prices from 100+ exchanges for accuracy
 */
async function fetchCryptoComparePrices(symbols: string[]): Promise<Record<string, number>> {
  if (symbols.length === 0) {
    return {};
  }

  try {
    // CryptoCompare supports batch queries: fsyms=BTC,ETH,RVN&tsyms=USD
    const symbolsParam = symbols.map(s => s.toUpperCase()).join(',');
    const url = `https://min-api.cryptocompare.com/data/pricemulti?fsyms=${symbolsParam}&tsyms=USD`;
    
    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('CryptoCompare API error:', response.status, response.statusText);
      return {};
    }

    const data = await response.json();
    
    // Response format: { "BTC": { "USD": 93478.69 }, "ETH": { "USD": 3201.05 } }
    const prices: Record<string, number> = {};
    const now = Date.now();
    
    for (const [symbol, priceData] of Object.entries(data)) {
      if (priceData && typeof priceData === 'object' && 'USD' in priceData) {
        const usdPrice = (priceData as any).USD;
        if (typeof usdPrice === 'number' && usdPrice > 0) {
          prices[symbol] = usdPrice;
          
          // Update cache
          priceCache.prices[symbol] = {
            usd: usdPrice,
            last_updated_at: now,
          };
        }
      }
    }

    if (Object.keys(prices).length > 0) {
      priceCache.lastUpdate = now;
    }
    
    return prices;
  } catch (error) {
    console.error('Error fetching prices from CryptoCompare:', error);
    return {};
  }
}

/**
 * Get price for a single token (uses cache if fresh)
 */
export async function getTokenPrice(symbol: string): Promise<number | null> {
  const upperSymbol = symbol.toUpperCase();

  // Stablecoins always return $1
  if (['USDT', 'USDC', 'DAI', 'BUSD'].includes(upperSymbol)) {
    return 1.0;
  }

  // Check cache first
  const now = Date.now();
  if (priceCache.prices[upperSymbol] && now - priceCache.lastUpdate < CACHE_DURATION) {
    return priceCache.prices[upperSymbol].usd;
  }

  // Fetch fresh price
  const prices = await fetchCryptoComparePrices([upperSymbol]);
  return prices[upperSymbol] || null;
}

/**
 * Get prices for multiple tokens at once (more efficient with batch API)
 */
export async function getTokenPrices(symbols: string[]): Promise<Record<string, number>> {
  const upperSymbols = symbols.map(s => s.toUpperCase());
  const now = Date.now();

  // Check if cache is fresh
  if (now - priceCache.lastUpdate < CACHE_DURATION) {
    const cachedPrices: Record<string, number> = {};
    let allCached = true;
    
    for (const symbol of upperSymbols) {
      if (priceCache.prices[symbol]) {
        cachedPrices[symbol] = priceCache.prices[symbol].usd;
      } else {
        allCached = false;
      }
    }
    
    // If all symbols are cached, return immediately
    if (allCached && Object.keys(cachedPrices).length === upperSymbols.length) {
      return cachedPrices;
    }
  }

  // Fetch fresh prices for all symbols (single batch call)
  return await fetchCryptoComparePrices(upperSymbols);
}

/**
 * Get all cached prices (useful for displaying multiple prices without API calls)
 */
export function getCachedPrices(): Record<string, number> {
  const prices: Record<string, number> = {};
  for (const [symbol, data] of Object.entries(priceCache.prices)) {
    prices[symbol] = data.usd;
  }
  return prices;
}

/**
 * Preload prices for common tokens (call on app init)
 * @param customTokenSymbols - Optional array of custom token symbols to include
 */
export async function preloadCommonPrices(customTokenSymbols?: string[]): Promise<void> {
  const commonTokens = ['BTC', 'ETH', 'RVN', 'LTC', 'DOGE', 'XRP', 'USDT', 'USDC', 'BNB', 'SOL', 'MATIC', 'AVAX', 'ADA'];
  
  // Add custom token symbols
  const allTokens = customTokenSymbols 
    ? [...commonTokens, ...customTokenSymbols.map(s => s.toUpperCase())]
    : commonTokens;
  
  await getTokenPrices(allTokens);
}

/**
 * Format price to appropriate decimal places
 */
export function formatPrice(price: number): string {
  if (price === 0) {
    return '0.00';
  }
  if (price >= 1000) {
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else if (price >= 1) {
    return price.toFixed(2);
  } else if (price >= 0.001) {
    // For small prices like RVN ($0.008), show 4 decimals
    return price.toFixed(4);
  } else if (price >= 0.00001) {
    // For very small prices, show 6 decimals
    return price.toFixed(6);
  } else {
    // For ultra-small prices, show 8 decimals
    return price.toFixed(8);
  }
}

/**
 * Calculate USD value for a token amount
 */
export async function calculateUSDValue(symbol: string, amount: number): Promise<number | null> {
  const price = await getTokenPrice(symbol);
  if (price === null) return null;
  return price * amount;
}

/**
 * Format USD value with $ sign
 */
export function formatUSD(value: number): string {
  if (value >= 1000) {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } else {
    return `$${value.toFixed(2)}`;
  }
}

/**
 * Check if price data is stale and needs refresh
 */
export function isPriceDataStale(): boolean {
  const now = Date.now();
  return now - priceCache.lastUpdate > CACHE_DURATION;
}

/**
 * Force refresh all cached prices
 */
export async function refreshPrices(): Promise<void> {
  const symbols = Object.keys(priceCache.prices);
  if (symbols.length > 0) {
    await fetchCryptoComparePrices(symbols);
  } else {
    await preloadCommonPrices();
  }
}
